import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ShieldCheck, Check, Minus, X, ArrowRight } from 'lucide-react';

const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Verifica se o usuário já registrou sua preferência de cookies
    const consent = localStorage.getItem('literafan_cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => {
        setVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // 1. Aceitar Todos os Cookies
  const handleAcceptAll = () => {
    localStorage.setItem(
      'literafan_cookie_consent',
      JSON.stringify({
        choice: 'all',
        essential: true,
        analytics: true,
        advertising: true,
        timestamp: new Date().toISOString(),
      })
    );
    setVisible(false);
  };

  // 2. Aceitar Parcialmente (Apenas essenciais e analíticos)
  const handleAcceptPartial = () => {
    localStorage.setItem(
      'literafan_cookie_consent',
      JSON.stringify({
        choice: 'partial',
        essential: true,
        analytics: true,
        advertising: false,
        timestamp: new Date().toISOString(),
      })
    );
    setVisible(false);
  };

  // 3. Rejeitar (Apenas cookies estritamente necessários para funcionamento)
  const handleReject = () => {
    localStorage.setItem(
      'literafan_cookie_consent',
      JSON.stringify({
        choice: 'rejected',
        essential: true,
        analytics: false,
        advertising: false,
        timestamp: new Date().toISOString(),
      })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside
      aria-label="Aviso de Consentimento de Cookies"
      className="fixed bottom-3 left-3 right-3 sm:bottom-5 sm:left-6 sm:right-6 md:left-auto md:right-6 md:max-w-xl z-50 animate-fade-in-up"
    >
      <div className="bg-[#0e0e0e]/95 border border-stone-800 rounded-2xl p-5 shadow-2xl backdrop-blur-xl text-stone-200 font-sans">
        {/* Cabeçalho do Banner com Ícone */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
            <Cookie size={19} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-bold text-white tracking-wide">
                Utilização de Cookies
              </h4>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-800 text-stone-300 font-mono">
                LGPD
              </span>
            </div>
            <p className="text-xs text-stone-300 mt-1 leading-relaxed">
              Este site utiliza cookies para garantir seu funcionamento básico, aprimorar a sua experiência de navegação literária, mensurar estatísticas de acesso e viabilizar parcerias com fornecedores de terceiros (como o Google).
            </p>
          </div>
        </div>

        {/* Linha com Botão para Ler a Política Completa */}
        <div className="mb-4 pt-2 border-t border-stone-800/80 flex items-center justify-between">
          <span className="text-[11px] text-stone-400">
            Deseja entender como tratamos cada categoria de cookie?
          </span>
          <Link
            to="/privacidade"
            onClick={() => setVisible(true)}
            className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors whitespace-nowrap pl-2"
          >
            <span>Ler Política de Privacidade</span>
            <ArrowRight size={13} />
          </Link>
        </div>

        {/* Grupo de Botões: Aceitar Todos, Aceitar Parcialmente e Rejeitar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {/* Botão Aceitar Todos */}
          <button
            type="button"
            onClick={handleAcceptAll}
            className="w-full py-2.5 px-3 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-primary/20 flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <Check size={14} />
            <span>Aceitar Todos</span>
          </button>

          {/* Botão Aceitar Parcialmente */}
          <button
            type="button"
            onClick={handleAcceptPartial}
            className="w-full py-2.5 px-3 bg-stone-800 hover:bg-stone-700 text-stone-200 border border-stone-700/80 text-xs font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            title="Aceita apenas cookies essenciais e estatísticos"
          >
            <Minus size={14} />
            <span>Aceitar Parcialmente</span>
          </button>

          {/* Botão Rejeitar */}
          <button
            type="button"
            onClick={handleReject}
            className="w-full py-2.5 px-3 bg-stone-900 hover:bg-rose-950/40 text-stone-400 hover:text-rose-300 border border-stone-800 hover:border-rose-900/50 text-xs font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            title="Rejeita cookies não essenciais de publicidade e rastreamento"
          >
            <X size={14} />
            <span>Rejeitar</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default CookieConsentBanner;
